import { z } from 'zod';
// TODO update to latest fsd linter to allow cross-imports
// eslint-disable-next-line boundaries/element-types
import { filtersValidator } from '@/features/filters';
import type {
  MetricColumnDataType,
  DimensionColumnDataType,
  DimensionDataFormatType,
  MetricDataFormatType,
} from '@/shared/types';
import {
  DateScale,
  Lifetime,
  serializedDateRangeValueObjectValidator,
} from '@/shared/types';

export enum ChartType {
  cohort = 'cohort',
  calendar = 'calendar',
  cohortHistory = 'cohortHistory',
}

export enum SeriesChartType {
  line = 'line',
  step = 'step',
  area = 'area',
  bar = 'bar',
}

export enum SeriesLineStyle {
  solid = 'solid',
  dashed = 'dashed',
  dotted = 'dotted',
}

export enum SeriesValueLabels {
  none = 'none',
  all = 'all',
  startEnd = 'startEnd',
}

export enum SeriesPercentDiff {
  none = 'none',
  fromFirst = 'fromFirst',
  fromPrevious = 'fromPrevious',
}

export const BY_METRIC_LINE_WIDTH = 'byMetric' as const;

export const FIXED_LINE_WIDTHS = [1.5, 2.5, 4] as const;

export const DEFAULT_LINE_WIDTH = 4;

export const VALUE_LABEL_FONT_SIZES = [10, 12, 14, 16, 18, 20, 22, 24] as const;

export const DEFAULT_VALUE_LABEL_FONT_SIZE = 14;

const axisSeriesSettingsValidator = (defaultLineStyle: SeriesLineStyle) =>
  z.object({
    chartType: z.nativeEnum(SeriesChartType).default(SeriesChartType.line),
    lineWidth: z
      .union([
        z.literal(1.5),
        z.literal(2.5),
        z.literal(4),
        z.literal(BY_METRIC_LINE_WIDTH),
      ])
      .default(DEFAULT_LINE_WIDTH),
    lineWidthMetric: z.string().default(''),
    lineStyle: z.nativeEnum(SeriesLineStyle).default(defaultLineStyle),
    valueLabels: z
      .nativeEnum(SeriesValueLabels)
      .default(SeriesValueLabels.all),
    valueLabelFontSize: z
      .union([
        z.literal(10),
        z.literal(12),
        z.literal(14),
        z.literal(16),
        z.literal(18),
        z.literal(20),
        z.literal(22),
        z.literal(24),
      ])
      .default(DEFAULT_VALUE_LABEL_FONT_SIZE),
    valueLabelBold: z.boolean().default(true),
    hideOverlappingLabels: z.boolean().default(true),
    percentDiff: z
      .nativeEnum(SeriesPercentDiff)
      .default(SeriesPercentDiff.none),
  });

export type AxisSeriesSettings = z.infer<
  ReturnType<typeof axisSeriesSettingsValidator>
>;

export const DEFAULT_LEFT_AXIS_SERIES: AxisSeriesSettings = {
  chartType: SeriesChartType.line,
  lineWidth: DEFAULT_LINE_WIDTH,
  lineWidthMetric: '',
  lineStyle: SeriesLineStyle.solid,
  valueLabels: SeriesValueLabels.all,
  valueLabelFontSize: DEFAULT_VALUE_LABEL_FONT_SIZE,
  valueLabelBold: true,
  hideOverlappingLabels: true,
  percentDiff: SeriesPercentDiff.none,
};

export const DEFAULT_RIGHT_AXIS_SERIES: AxisSeriesSettings = {
  ...DEFAULT_LEFT_AXIS_SERIES,
  lineStyle: SeriesLineStyle.dashed,
};

interface ColumnBaseConfig {
  id: string;
  group: string;
  name: string;
  description: string;
  order: number;
}
type MetricColumn = ColumnBaseConfig & {
  type: MetricColumnDataType;
  dataType: MetricDataFormatType;
};

type DimensionColumn = ColumnBaseConfig & {
  type: DimensionColumnDataType;
  dataType: DimensionDataFormatType;
};

type AxisConfig = Record<string, MetricColumn | DimensionColumn>;

export interface ChartMeta {
  defaultAxis: {
    dimension: string;
    primaryMetric: string;
    secondaryMetric: string;
  };
  config: AxisConfig;
  dimensions: string[];
  metrics: string[];
}

export enum SplitSortBy {
  name = 'name',
  value = 'value',
}

export enum SplitSortDirection {
  desc = 'desc',
  asc = 'asc',
}

const splitSortValidator = z.object({
  by: z.nativeEnum(SplitSortBy).default(SplitSortBy.name),
  metric: z.string().default(''),
  direction: z.nativeEnum(SplitSortDirection).default(SplitSortDirection.desc),
});

export type SplitSort = z.infer<typeof splitSortValidator>;

export const DEFAULT_SPLIT_SORT: SplitSort = {
  by: SplitSortBy.name,
  metric: '',
  direction: SplitSortDirection.desc,
};

export const calendarChartDataConfigValidator = z.object({
  id: z.string(),
  type: z.literal(ChartType.calendar),
  cohortDateScale: z.nativeEnum(DateScale),
  cohortDateRange: serializedDateRangeValueObjectValidator,
  revenueDateScale: z.nativeEnum(DateScale),
  revenueDateRange: serializedDateRangeValueObjectValidator,
  probabilisticAttribution: z.boolean(),
  splitDimension: z.string(),
  primaryMetric: z.string(),
  secondaryMetric: z.string(),
  excludedSplitOptions: z.array(z.string()),
  syncedYAxes: z.boolean(),
  highlightLineOnHover: z.boolean().default(true),
  leftAxisSeries: axisSeriesSettingsValidator(SeriesLineStyle.solid).default(
    () => ({ ...DEFAULT_LEFT_AXIS_SERIES }),
  ),
  rightAxisSeries: axisSeriesSettingsValidator(SeriesLineStyle.dashed).default(
    () => ({ ...DEFAULT_RIGHT_AXIS_SERIES }),
  ),
  splitSort: splitSortValidator.optional(),
  filters: filtersValidator,
});

export const cohortChartDataConfigValidator = z.object({
  id: z.string(),
  type: z.literal(ChartType.cohort),
  dateScale: z.nativeEnum(DateScale),
  dateRange: serializedDateRangeValueObjectValidator,
  lifetime: z.nativeEnum(Lifetime),
  probabilisticAttribution: z.boolean(),
  splitDimension: z.string(),
  primaryMetric: z.string(),
  secondaryMetric: z.string(),
  excludedSplitOptions: z.array(z.string()),
  syncedYAxes: z.boolean(),
  highlightLineOnHover: z.boolean().default(true),
  leftAxisSeries: axisSeriesSettingsValidator(SeriesLineStyle.solid).default(
    () => ({ ...DEFAULT_LEFT_AXIS_SERIES }),
  ),
  rightAxisSeries: axisSeriesSettingsValidator(SeriesLineStyle.dashed).default(
    () => ({ ...DEFAULT_RIGHT_AXIS_SERIES }),
  ),
  splitSort: splitSortValidator.optional(),
  filters: filtersValidator,
});

export const cohortHistoryChartDataConfigValidator = z.object({
  id: z.string(),
  type: z.literal(ChartType.cohortHistory),
  snapshotDateScale: z.nativeEnum(DateScale),
  snapshotDateRange: serializedDateRangeValueObjectValidator,
  cohortDateScale: z.nativeEnum(DateScale),
  cohortDateRange: serializedDateRangeValueObjectValidator,
  lifetime: z.nativeEnum(Lifetime),
  probabilisticAttribution: z.boolean(),
  splitDimension: z.string(),
  primaryMetric: z.string(),
  secondaryMetric: z.string(),
  excludedSplitOptions: z.array(z.string()),
  syncedYAxes: z.boolean(),
  highlightLineOnHover: z.boolean().default(true),
  showOnlyFullCohorts: z.boolean().default(true),
  filters: filtersValidator,
  leftAxisSeries: axisSeriesSettingsValidator(SeriesLineStyle.solid).default(
    () => ({ ...DEFAULT_LEFT_AXIS_SERIES }),
  ),
  rightAxisSeries: axisSeriesSettingsValidator(SeriesLineStyle.dashed).default(
    () => ({ ...DEFAULT_RIGHT_AXIS_SERIES }),
  ),
  splitSort: splitSortValidator.optional(),
});

export type CohortChartConfig = z.infer<typeof cohortChartDataConfigValidator>;

export type CalendarChartConfig = z.infer<
  typeof calendarChartDataConfigValidator
>;

export type CohortHistoryChartConfig = z.infer<
  typeof cohortHistoryChartDataConfigValidator
>;

export type Chart =
  | CohortChartConfig
  | CalendarChartConfig
  | CohortHistoryChartConfig;

export const resolveSortMetric = (config: Chart): string => {
  const splitSort = 'splitSort' in config ? config.splitSort : undefined;

  return splitSort?.metric || config.primaryMetric;
};

export const VIEW_ONLY_CONFIG_KEYS = [
  'excludedSplitOptions',
  'syncedYAxes',
  'highlightLineOnHover',
  'showOnlyFullCohorts',
  'leftAxisSeries',
  'rightAxisSeries',
  'splitSort',
] as const;

export type ViewOnlyConfigKey = (typeof VIEW_ONLY_CONFIG_KEYS)[number];

type OmitViewOnlyKeys<T> = T extends unknown
  ? Omit<T, ViewOnlyConfigKey>
  : never;

export type RequestedConfig<
  T extends { excludedSplitOptions: string[]; syncedYAxes: boolean },
> = OmitViewOnlyKeys<T>;

export interface DefaultAxesParam {
  dimension: string;
  primaryMetric: string;
  secondaryMetric: string;
}

type DataPoint<T extends string> = { [key in T]: string } & {
  data: Record<string, Record<string, number>>;
};

type Timeseries<T extends string> = DataPoint<T>[];

export interface DataResponseBase<T extends string> {
  data: Timeseries<T>;
  splitOptions: Record<string, string[]>;
}
